import { useContext, useLayoutEffect, useMemo } from "react";

import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { BTLocalStorage } from "types/btStorage";

import { type IOAuthStateParamState } from "commonComponents/auth/AuthStoreDiscovery.types";
import { redirectToAuth0User } from "commonComponents/auth/AuthStoreDiscovery.utils";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTLoading } from "commonComponents/utilities/BTLoading/BTLoading";

import { AuthStoreTypes } from "entity/login/EmailUsernameEntry/EmailUsernameEntry.types";

export interface IIdpReAuthProps {
    oauthState: IOAuthStateParamState | null;
    isOAuthFlow: boolean;
    children: React.ReactNode;
}

export const IdpReAuth = ({ oauthState, isOAuthFlow, children }: IIdpReAuthProps) => {
    const userInfo = useContext(UserInfoContext);

    const previousLoginInfo = useMemo(
        () => BTLocalStorage.get("bt-object-previousAuthStoreInfo"),
        [],
    );

    const username = previousLoginInfo?.username;

    // lpr (last page requested) in the query string means the user was redirected here
    // to reauthenticate — bypass the session guard so Auth0 redirect always fires
    const hasLastPageRequested = useMemo(
        () => !!new URLSearchParams(window.location.search).get("lpr"),
        [], // URL is stable during component lifecycle
    );

    const shouldRedirectToAuth0 = useMemo(
        () => !isOAuthFlow && (!userInfo?.globalUserId || hasLastPageRequested),
        [isOAuthFlow, userInfo?.globalUserId, hasLastPageRequested],
    );

    const shouldRedirectAuth0User = useMemo(
        () =>
            shouldRedirectToAuth0 &&
            previousLoginInfo?.authStore === AuthStoreTypes.Auth0 &&
            !!username,
        [shouldRedirectToAuth0, previousLoginInfo, username],
    );

    useLayoutEffect(() => {
        if (shouldRedirectAuth0User && username) {
            // Known Auth0 user — pass username hint so Auth0 can pre-fill the login form
            redirectToAuth0User(username, oauthState);
        } else if (shouldRedirectToAuth0) {
            // Unknown user (no previous Auth0 login on this device) — still route through
            // redirectToAuth0User so the oauthState (with lastPageRequested / lpr) is preserved
            // through the round-trip and the user lands back on the page they originally requested.
            redirectToAuth0User("", oauthState);
        }
    }, [shouldRedirectAuth0User, shouldRedirectToAuth0, username, oauthState]);

    // Suppress children while redirect is pending to avoid flicker
    return shouldRedirectAuth0User || shouldRedirectToAuth0 ? (
        <BTRow justify="center">
            <BTLoading />
        </BTRow>
    ) : (
        <>{children}</>
    );
};

export default IdpReAuth;
